import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { CompanyService } from '../../../services/company.service';
import { AuthService } from '../../../services/auth.service';
import { AdvanceBillingInvoiceService } from '../../../services/advance-billing-invoice.service';
import { CompanyBillingStatusById } from '../../models/advance-billing-invoice.model';

@Component({
  selector: 'app-subscription-alert',
  standalone: true,
  imports: [CommonModule, ButtonModule, MessageModule, ConfirmDialogModule, ToastModule],
  providers: [ConfirmationService, MessageService],
  templateUrl: './subscription-alert.component.html',
  styleUrls: ['./subscription-alert.component.scss']
})
export class SubscriptionAlertComponent implements OnInit, OnDestroy {
  private readonly companyService = inject(CompanyService);
  private readonly authService = inject(AuthService);
  private readonly advanceBillingService = inject(AdvanceBillingInvoiceService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);

  private subscriptions = new Subscription();

  companyBillingStatus: CompanyBillingStatusById | null = null;
  isLoadingBillingStatus = false;
  isProcessingPayment = false;
  private companysKey: string | null = null;

  ngOnInit(): void {
    // Don't show for SuperAdmin
    if (this.isSuperAdmin()) {
      return;
    }

    this.subscriptions.add(
      this.companyService.getSelectedCompanySkey().subscribe((sKey) => {
        this.companysKey = sKey || null;
        if (this.companysKey) {
          this.loadCompanyBillingStatus(this.companysKey);
        } else {
          this.companyBillingStatus = null;
          this.isLoadingBillingStatus = false;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }

  shouldShowAlert(): boolean {
    // Don't show for SuperAdmin and only show when subscription is Suspended
    return !this.isSuperAdmin() && this.companyBillingStatus?.subscriptionType === 'Suspended';
  }

  private loadCompanyBillingStatus(companyId: string): void {
    this.isLoadingBillingStatus = true;
    this.advanceBillingService.getCompanyBillingStatusById(companyId).subscribe({
      next: (res) => {
        this.companyBillingStatus = res?.data || null;
        this.isLoadingBillingStatus = false;
      },
      error: () => {
        this.companyBillingStatus = null;
        this.isLoadingBillingStatus = false;
      }
    });
  }

  onRenewSubscription(): void {
    if (!this.companysKey) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Company Selected',
        detail: 'Please select a company first',
        life: 3000
      });
      return;
    }

    this.confirmationService.confirm({
      message: 'Are you sure you want to renew your subscription?',
      header: 'Confirm Subscription Renewal',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-primary',
      accept: () => {
        this.executePayment();
      }
    });
  }

  private executePayment(): void {
    if (!this.companysKey) return;

    this.isProcessingPayment = true;

    this.advanceBillingService
      .generateAdvancedBillingInvoices({
        companyId: this.companysKey,
        force: false,
        isManualTrigger: true
      })
      .subscribe({
        next: () => {
          this.isProcessingPayment = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Subscription Renewed',
            detail: 'Your subscription has been renewed successfully',
            life: 5000
          });
          // Reload billing status to update the alert visibility
          if (this.companysKey) {
            this.loadCompanyBillingStatus(this.companysKey);
          }
        },
        error: (error) => {
          this.isProcessingPayment = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Renewal Failed',
            detail: error.error?.message || 'An error occurred while renewing the subscription',
            life: 5000
          });
        }
      });
  }
}
